// src/App.tsx
import {ThemeProvider} from 'styled-components';
import {GlobalStyle} from './styles/global';
import {theme} from './styles/theme';
import {Routes, Route, useNavigate, Outlet} from 'react-router-dom';

import NavBar from './pages/home/NavBar';
import {Home} from './pages/home/Home';
import Login from './pages/auths/login';
import Signup from './pages/auths/signup';
import ListingsPage from './stalls/dashboard/stall_dashboard';
import React, {useState, useEffect, createContext, useContext, useMemo} from 'react';
import Profile from './pages/registeredUsers/profile';
import {Footer} from './components/layout/Footer';
import {CreatePremiumAdPage} from './pages/registeredUsers/premuimAds/CreatePremiumAdPage';
import OtpVerificationPage from './pages/auths/OtpVerificationPage';
import {onAuthStateChanged, User, signOut} from 'firebase/auth';
import AdminDashboard from './pages/administrators/AdminDashboard'; // Make sure this path is correct
import RealEstateManagement from './pages/administrators/RealEstateManagement';
import {auth, db} from './firebase';
import {ListingDetail} from './pages/home/ListingDetail';
import {MessagesUI} from './pages/registeredUsers/messages';
import 'leaflet/dist/leaflet.css';
import BuyStall from './pages/registeredUsers/BuyStall';
import PostUploadPage from './pages/posts/PostUploadPage';
import StallLogin from './pages/auths/stalllogin';
import StallRenewalPage from './pages/registeredUsers/StallRenewalPage';
import {StallDetailPage} from './pages/home/detailsPages/StallDetailPage';
import PaymentCallback from './pages/payments/PaymentCallback';
import PaymentCancelled from './pages/payments/PaymentCancelled';
import UserSaleSubmission from './pages/inquiries/UserSaleSubmission';
import UserPurchaseRequest from './pages/inquiries/UserPurchaseRequest';
import StallRenewalLogin from './pages/auths/renewallogin';
import AdminLogin from './pages/auths/adminlogin';
import AdminSetup from './pages/auths/AdminSetup';
import ManualPage from './About_us/userManual';
import WhyGrema from './About_us/why_grema';
import PolicyPage from './About_us/our_policy';
import ServicesPage from './About_us/services';
import Promotions from './About_us/promotions';
import ContactUs from './About_us/contact_us';
import { RealEstateDetailsPage } from './pages/home/detailsPages/RealEstateDetails';
import EditListingPage from './stalls/dashboard/EditListing';
import Suppliers from './pages/inquiries/supliers';
import ModeratorDashboard from './pages/moderator/ModeratorDashboard'; // Make sure this path is correct
import { FeedbackPage } from './pages/FeedBackPage';

// --- Auth Context Setup ---
interface AuthContextType {
  currentUser: User | null;
  isLoggedIn: boolean;
  loading: boolean;
  logout: () => Promise<void>;
  isAdmin: boolean;
  setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>;
  isModerator: boolean; // 🆕 Added isModerator
  setIsModerator: React.Dispatch<React.SetStateAction<boolean>>; // 🆕 Added setIsModerator
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
 }
  return context;
};

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false); // 🆕 Initialize state
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsLoggedIn(true);
        setCurrentUser(user);
        // Roles should be checked in Firestore/custom claims if session is persisted
        // For security, we reset them here and rely on the login process to set them correctly
        // onAuthStateChange might not immediately have the role, so leave it at false here.
        setIsAdmin(false);
        setIsModerator(false); // 🆕 Reset on state change
     } else {
        setIsLoggedIn(false);
        setCurrentUser(null);
        setIsAdmin(false);
        setIsModerator(false); // 🆕 Reset on logout
     }
      setLoading(false);
   });
    return () => unsubscribe();
 }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      setIsModerator(false);
      console.log("User logged out successfully!");
   } catch (error) {
      console.error("Error logging out:", error);
      throw error;
   }
 };

  return (
    <AuthContext.Provider value={{currentUser, isLoggedIn, loading, logout, isAdmin, setIsAdmin, isModerator, setIsModerator}}>
      {children}
    </AuthContext.Provider>
  );
};

// --- Protected Route Component (Standard user) ---
const ProtectedRoute: React.FC = () => {
  const {isLoggedIn, loading} = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isLoggedIn) {
      navigate('/login', {replace: true});
   }
 }, [isLoggedIn, loading, navigate]);

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '1.2rem', color: theme.colors.primary}}>
        Loading user session...
      </div>
    );
 }

  return isLoggedIn ? <Outlet /> : null;
};

// --- Admin Protected Route Component ---
const AdminProtectedRoute: React.FC = () => {
  // 🚫 Check for either Admin or Moderator for staff access, but only allow Admin to proceed to /admin
  const {isAdmin, loading} = useAuth(); 
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/admin/login', {replace: true});
   }
 }, [isAdmin, loading, navigate]);

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '1.2rem', color: theme.colors.primary}}>
        Verifying admin access...
      </div>
    );
 }

  return isAdmin ? <Outlet /> : null;
};

// --- Moderator Protected Route Component 🆕 ---
const ModeratorProtectedRoute: React.FC = () => {
  const {isModerator, loading} = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isModerator) {
      navigate('/admin/login', {replace: true}); // Redirect staff to the same login page
   }
 }, [isModerator, loading, navigate]);

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '1.2rem', color: theme.colors.primary}}>
        Verifying moderator access...
      </div>
    );
 }

  return isModerator ? <Outlet /> : null;
};


// --- Main App Component ---
export const App = () => {
  const {isLoggedIn, logout, isAdmin, setIsAdmin, isModerator, setIsModerator} = useAuth(); // 🆕 Get isModerator/setIsModerator
  const navigate = useNavigate();

  // State for search and category
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');

// Handle staff login (Admin or Moderator) 🆕 Refactored to handle roles
   const handleStaffLogin = (role: 'admin' | 'moderator' | 'superadmin') => {
    if (role === 'admin' || role === 'superadmin') {
      setIsAdmin(true);
      setIsModerator(false);
      navigate('/admin');
    } else if (role === 'moderator') {
      setIsModerator(true);
      setIsAdmin(false);
      navigate('/moderator');
    } else {
      // Fallback if role is unexpectedly set or missing
      console.error('Unknown staff role during login.');
      setIsAdmin(false);
      setIsModerator(false);
      logout(); // Logout unrecognised staff
    }
  };

  // Handle staff logout (same function for both roles)
  const handleStaffLogout = () => {
    logout(); // Calls the logout in AuthContext which resets all states
    navigate('/');
 };

  // Memoize NavBar props to prevent unnecessary re-renders of NavBar
  const navBarProps = useMemo(() => ({
    searchQuery: searchQuery,
    setSearchQuery: setSearchQuery,
    selectedCategory: selectedCategory,
    isLoggedIn: isLoggedIn,
    isAdmin: isAdmin || isModerator, // Treat staff as admin in NavBar for display purposes
      onLogin: () => navigate('/login'), // Corrected to navigate to /login
    onSignup: () => navigate('/signup'),
    onLogout: logout, 
    onAdminLogin: () => navigate('/admin/login'),
    onAdminLogout: handleStaffLogout,
    onBuyStall: () => navigate('/buy-stall'),
    onPost: () => navigate('/stall-login'),
    onProfile: () => navigate('/profile'),
    onHomeClick: () => {
      navigate('/');
      setSelectedCategory('All Categories'); 
      setSearchQuery(''); // Clear search query on home click
   },
 }), [searchQuery, selectedCategory, isLoggedIn, isAdmin, isModerator, navigate, logout, handleStaffLogout]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <NavBar {...navBarProps} /> {/* Pass navBarProps to NavBar */}
      <Routes>
        <Route path="/" element={<Home
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/otp-verification" element={<OtpVerificationPage />} />
        <Route path="/listing/:id" element={<ListingDetail />} />
        
{/* Admin Setup Route - only shown when no admins exist */}
         <Route path="/admin/setup" element={<AdminSetup />} />
         
         {/* 🔑 Updated AdminLogin to use the new role-aware handler */}
         <Route path="/admin/login" element={<AdminLogin onLogin={handleStaffLogin} />} />

        {/* Protected Routes (Standard user) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/profile" element={<Profile />} />
          <Route path="/premiumAds" element={<CreatePremiumAdPage />} />
          <Route path="/user-listings" element={<ListingsPage />} />
          <Route path="/messages" element={<MessagesUI />} />
          <Route path="/buy-stall" element={<BuyStall />} />
          <Route path="/stall-login" element={<StallLogin />} />
          <Route path="/post" element={<PostUploadPage />} />
          <Route path="/renewal-login" element={<StallRenewalLogin />} />
          <Route path="/stall-renewal/:stallNumber" element={<StallRenewalPage />} />    
          <Route path="/renew-stall" element={<StallRenewalPage />} />
          <Route path="/dashboard/edit-listing/:id" element={<EditListingPage />} />
          <Route path="/stalls/:stallId" element={<StallDetailPage />} />   
          <Route path="/real-estate/:category/:id" element={<RealEstateDetailsPage />} />
          <Route path="/payment-callback" element={<PaymentCallback />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          <Route path="/buy-from-grema" element={<UserPurchaseRequest />} />
          <Route path="/sell-to-grema" element={<UserSaleSubmission />} />
          <Route path="/suppliers" element={<Suppliers />} /> 
          <Route path="/otp" element={<UserSaleSubmission />} />
        </Route>

        {/* Admin Protected Routes */}
        <Route element={<AdminProtectedRoute />}>
          <Route path="/admin" element={<AdminDashboard onLogout={handleStaffLogout} />} />
          <Route path="/admin/real-estate" element={<RealEstateManagement />} />
        </Route>

        {/* Moderator Protected Routes 🆕 FIX APPLIED */}
        <Route element={<ModeratorProtectedRoute />}>
          <Route 
             path='/moderator' 
             element={
                <ModeratorDashboard 
                   onLogout={handleStaffLogout} 
                   isModerator={true} 
                />
             }
          />
        </Route>

        {/* Nav tabs */}
         <Route path="/usermanual" element={<ManualPage />} />
         <Route path="/why-grema" element={<WhyGrema />} />
          <Route path="policy" element={<PolicyPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/promotions" element={<Promotions />} />
          <Route path="/contact" element={<ContactUs />} />
          
        {/* Feedback */}
        <Route path="/feedback" element={<FeedbackPage/>}/>
      </Routes>
      <Footer />
    </ThemeProvider>
  );
};